import CartItemRow from '~/components/cart-item-row';
import CartRewardsCallout from '~/components/cart-rewards-callout';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useCart } from '~/context/cart-context';
import {
  getFillerImage,
  needsLightLogoTile,
} from '~/utils/get-filler-image';
import { Link, useNavigate } from '@remix-run/react';
import { toast } from 'react-hot-toast';
import { getTaxRate } from '~/utils/get-taxes';
import { useModal } from '~/context/modal-context';
import { trackBeginCheckout, trackViewCart, analytics, klaviyo } from '~/utils/analytics';
import { useFocusTrap } from '~/hooks/use-focus-trap';
import { useBrandLogoCache } from '~/context/brand-logo-context';
import { parseCartError, isErrorAlreadyHandled } from '~/utils/cart-error-handler';
import { useStore } from '~/context/store-context';
import { executeCartOperation } from '~/utils/cart-helper';

import { cartFetch } from '~/utils/cart-queue';
// Bundle MUP data structure
interface BundleMup {
  active: boolean;
  products: string[];
  rule_config: {
    quantity?: number;
    target_price?: number;
    discount_percent?: number;
    discount_amount?: number;
    minimum_spend?: number;
  };
  rule_id: string;
  rule_name: string;
  rule_type: string;
}

// Bundle product type for displaying in cart
interface BundleProductItem {
  id: string;
  name: string;
  image: string;
  price: number;
  treez_id: string;
  inStock: boolean;
}

interface CartItem {
  id: string; // orderline ID
  order_id: string; // needed for "update" mutation
  product_id: string; // needed for "update" mutation
  treez_id?: string; // treez product ID for bundle matching
  onsale?: boolean;
  primary_image: string;
  budflow_image_url?: string;
  name: string;
  name_sanitized?: string;
  brand: string;
  price: number;
  price_sell: number;
  quantity: number;
  classification: string;
  category: string;
  budflow_mup?: BundleMup;
}

/**
 * Reconcile a fresh server cart (`incoming`) against the current display list
 * (`prev`) so the on-screen order stays STABLE across GETs.
 *
 * Why: the budflow backend regenerates every orderline's UUIDv7 id on each
 * addtocart, so the server's row order is volatile and reshuffles between reads.
 * Sorting by id therefore makes items jump. Instead we key on the stable
 * `product_id` (catalog UUID) — products already visible keep their position;
 * genuinely-new products (including a just-added optimistic line, matched by
 * name since it has no product_id yet) append at the bottom.
 */
function mergeStableCartOrder(prev: CartItem[], incoming: CartItem[]): CartItem[] {
  const keyOf = (it: CartItem) =>
    it.product_id
      ? `pid:${it.product_id}`
      : `name:${(it.name_sanitized || it.name || '').toLowerCase()}`;

  const result: CartItem[] = [];
  const placed = new Set<string>();

  // 1) Preserve the current order for products still present in the server list.
  for (const p of prev) {
    const match = incoming.find((s) => s.product_id && s.product_id === p.product_id);
    if (match && !placed.has(keyOf(match))) {
      result.push(match);
      placed.add(keyOf(match));
    }
  }
  // 2) Append any server products not yet placed — genuinely new, at the bottom.
  for (const s of incoming) {
    if (!placed.has(keyOf(s))) {
      result.push(s);
      placed.add(keyOf(s));
    }
  }
  return result;
}

// Bundle analysis result
interface BundleAnalysis {
  bundleId: string;
  bundleName: string;
  requiredQuantity: number;
  currentQuantity: number;
  bundlePrice: number;
  regularPrice: number;
  originalPrice: number; // Original price before any discounts (price_sell)
  savings: number; // Incremental savings (for calculation)
  savingsFromOriginal: number; // Total savings from original price (for display)
  savingsPercent: number;
  isComplete: boolean;
  itemsInCart: CartItem[];
  missingCount: number;
  ruleType: string;
  discountPercent?: number;
  discountAmount?: number;
  minimumSpend?: number;
  currentSpend?: number;
  targetPrice?: number; // The fixed price for buy_x_get_for_y bundles
  bundleProductTreezIds: string[];
  cartTreezIds: string[];
}

// Helper function to get a cookie by name
const getCookie = (name: string) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

export default function SideCart() {
  const { isCartOpen, closeCart, setCartCount, incrementCount, decrementCount } = useCart();
  const { closeProductModal, openProductModal } = useModal();
  const { selectedStore } = useStore();
  const navigate = useNavigate();
  const TAX_RATE = getTaxRate();
  const { getBrandLogo } = useBrandLogoCache();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [liveMessage, setLiveMessage] = useState<string>('');

  // Clear each announcement shortly after it's spoken so the (in-dialog) live region never
  // holds stale text to be read when focus lands on the drawer. Safe against re-announcement
  // because the open message is gated to the open transition, not cart changes.
  useEffect(() => {
    if (!liveMessage) return;
    const t = setTimeout(() => setLiveMessage(''), 1200);
    return () => clearTimeout(t);
  }, [liveMessage]);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [promotionCode, setPromotionCode] = useState<string | null>(null);

  // Reset checkout loading state when returning via bfcache (browser back button)
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setIsCheckoutLoading(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);
  // focusContainer: land focus on the drawer itself on open (so the screen reader announces
  // the cart + its contents, not the close button). announceOnOpen: false because the dialog's
  // own name + aria-describedby (below) carry the announcement — avoids a duplicate.
  const sideCartRef = useFocusTrap(isCartOpen, {
    focusContainer: true,
    announceOnOpen: false,
  });
  const previousFocusElement = useRef<HTMLElement | null>(null);
  const hasTrackedViewCart = useRef(false);
  // True while the cart is open — used to fire the open/close announcement only on the
  // transition, not on every cart change (otherwise each quantity update re-announces it).
  const hasAnnouncedOpenRef = useRef(false);

  // Bundle-related state
  const [bundleDataLoaded, setBundleDataLoaded] = useState(false);
  const [bundleMupCache, setBundleMupCache] = useState<
    Map<string, { treez_id: string; budflow_mup: BundleMup }>
  >(new Map());
  const [bundleProductsMap, setBundleProductsMap] = useState<Map<string, BundleProductItem[]>>(
    new Map(),
  );
  const [loadingBundleProducts, setLoadingBundleProducts] = useState<Set<string>>(new Set());
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [addedProducts, setAddedProducts] = useState<Set<string>>(new Set());
  const [expandedBundles, setExpandedBundles] = useState<Set<string>>(new Set());

  // Toggle bundle expansion
  const toggleBundleExpanded = (bundleId: string) => {
    setExpandedBundles((prev) => {
      const next = new Set(prev);
      if (next.has(bundleId)) {
        next.delete(bundleId);
      } else {
        next.add(bundleId);
      }
      return next;
    });
  };

  // Announce (and capture/restore focus) only on the cart open/close TRANSITION — deps are
  // [isCartOpen] only, so quantity changes never re-fire this. On open we fetch the latest
  // items first and announce with that fresh result, so the just-added item is included
  // (avoids a premature "cart is empty" on the first open).
  useEffect(() => {
    let cancelled = false;

    if (isCartOpen) {
      hasAnnouncedOpenRef.current = true;
      previousFocusElement.current = document.activeElement as HTMLElement;

      (async () => {
        const items: CartItem[] = (await fetchCartItems()) || [];
        if (cancelled) return;
        const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
        const cartTotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const finalTotal = cartTotal + cartTotal * TAX_RATE;

        // 🔥 GA4 + Klaviyo view_cart — once per open
        if (items.length > 0 && !hasTrackedViewCart.current) {
          hasTrackedViewCart.current = true;
          klaviyo.viewCart({ total: finalTotal, itemCount, items });
          trackViewCart({ total: finalTotal, itemCount, items });
        }

        // Announcement 2 — the DETAIL, fired AFTER the fetch resolves so a just-added item is
        // included (aria-describedby is read too early and would report "empty"). Includes the
        // count so NVDA/JAWS users hear it (VoiceOver adds its own count; other readers don't).
        setLiveMessage(
          itemCount === 0
            ? 'Your cart is empty.'
            : `${itemCount} item${itemCount === 1 ? '' : 's'}, total $${finalTotal.toFixed(2)} including tax.`,
        );
      })();
    } else if (hasAnnouncedOpenRef.current) {
      // Close transition (only runs once, since the flag was set on open).
      hasAnnouncedOpenRef.current = false;
      hasTrackedViewCart.current = false;
      if (previousFocusElement.current) {
        previousFocusElement.current.focus();
      }
      setLiveMessage('Shopping cart closed.');
    }

    return () => {
      cancelled = true;
    };
  }, [isCartOpen]);

  // Listen for custom close event from focus trap
  useEffect(() => {
    const handleClose = () => closeCart();
    document.addEventListener('close-focus-trap', handleClose);
    return () => document.removeEventListener('close-focus-trap', handleClose);
  }, [closeCart]);

  // Check for promotion cookie when cart opens (scroll lock handled by useFocusTrap)
  useEffect(() => {
    if (isCartOpen) {
      const promo = getCookie('promotionCode');
      setPromotionCode(promo);
    }
  }, [isCartOpen]);

  // Fetch cart items from API
  const fetchCartItems = async () => {
    try {
      const selectedStore = localStorage.getItem('selectedStore');
      const order_id = selectedStore
        ? localStorage.getItem(`token_${selectedStore}`) || 'default_order_id'
        : 'default_order_id';

      const body = {
        actionType: 'GET',
        filter: { order_id: { eq: order_id } },
      };

      const response = await cartFetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cart items: ${response.statusText}`);
      }

      const data = await response.json();
      const edges = data?.edges || [];
      const mapped = edges.map((edge: any) => {
        const node = edge.node;
        return {
          id: node.id,
          order_id: node.order_id,
          product_id: node.product_id,
          onsale: node?.product?.onsale,
          primary_image:
            node?.product?.budflow_image_url ||
            node?.product?.primary_image ||
            getFillerImage(
              node?.product?.brand || 'Unknown Brand',
              node?.product?.category_type,
              getBrandLogo(node?.product?.brand || 'Unknown Brand'),
            ),
          name: node?.product?.name || 'Unknown Product',
          name_sanitized: node?.product?.name_sanitized || 'Unknown Product',
          brand: node?.product?.brand || 'N/A',
          price: parseFloat(node?.product?.price) || 0,
          price_sell: parseFloat(node?.product?.price_sell) || 0,
          quantity: parseInt(node?.quantity, 10) || 1,
          classification: node?.product?.classification || 'N/A',
          category: node?.product?.category || node?.product?.category_type || 'N/A', // Added category mapping
        };
      });

      const count = edges.reduce(
        (sum: number, edge: any) => sum + (parseInt(edge.node.quantity, 10) || 0),
        0,
      );
      setCartCount(count);
      // The backend regenerates every orderline's UUIDv7 id on each addtocart
      // (all lines get a fresh timestamp per write), so the server's row order is
      // volatile and reshuffles between GETs. Keep the display order STABLE by
      // reconciling against the current list keyed on the *product_id* (the catalog
      // UUID, which does NOT change): already-shown products keep their position,
      // genuinely-new products append at the bottom. This is what stops the
      // just-added item from jumping when the GET syncs.
      setCartItems((prev) => mergeStableCartOrder(prev, mapped));

      // Fetch bundle data for any uncached items (non-blocking)
      fetchBundleDataForItems(mapped);
      return mapped;
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch bundle data for cart items from Typesense (with caching)
  const fetchBundleDataForItems = async (items: CartItem[]) => {
    if (items.length === 0) {
      setBundleDataLoaded(true);
      return;
    }

    const uniqueProductIds = [...new Set(items.map((item) => item.product_id))];

    // First, immediately apply any cached data to cart items
    const cachedProductIds = uniqueProductIds.filter((id) => bundleMupCache.has(id));
    if (cachedProductIds.length > 0) {
      setCartItems((prevItems) =>
        prevItems.map((item) => {
          const cached = bundleMupCache.get(item.product_id);
          if (cached) {
            return {
              ...item,
              treez_id: cached.treez_id,
              budflow_mup: cached.budflow_mup,
            };
          }
          return item;
        }),
      );
    }

    // Find products we need to fetch (not in cache)
    const uncachedProductIds = uniqueProductIds.filter((id) => !bundleMupCache.has(id));

    // If all products are cached, we're done
    if (uncachedProductIds.length === 0) {
      setBundleDataLoaded(true);
      return;
    }

    try {
      const storeKey = localStorage.getItem('selectedStore') || selectedStore;

      // Only fetch for uncached products
      const productPromises = uncachedProductIds.map(async (productId) => {
        try {
          const response = await fetch(`/api/product?productId=${productId}&store=${storeKey}`);
          if (!response.ok) return null;
          const data = await response.json();
          return {
            productId,
            treez_id: data.treez_id,
            budflow_mup: data.budflow_mup,
          };
        } catch (error) {
          console.error(`Error fetching bundle data for ${productId}:`, error);
          return null;
        }
      });

      const results = await Promise.all(productPromises);
      const newBundleData = results.filter(
        (r): r is { productId: string; treez_id: string; budflow_mup: BundleMup } => r !== null,
      );

      // Update the cache with new data
      if (newBundleData.length > 0) {
        setBundleMupCache((prevCache) => {
          const newCache = new Map(prevCache);
          newBundleData.forEach((r) => {
            newCache.set(r.productId, { treez_id: r.treez_id, budflow_mup: r.budflow_mup });
          });
          return newCache;
        });

        // Update cart items with new bundle data
        setCartItems((prevItems) =>
          prevItems.map((item) => {
            const newData = newBundleData.find((d) => d.productId === item.product_id);
            if (newData) {
              return {
                ...item,
                treez_id: newData.treez_id,
                budflow_mup: newData.budflow_mup,
              };
            }
            return item;
          }),
        );
      }

      setBundleDataLoaded(true);
    } catch (error) {
      console.error('Error fetching bundle data:', error);
      setBundleDataLoaded(true);
    }
  };

  // Fetch other products in a bundle that aren't in the cart
  const fetchBundleProducts = useCallback(
    async (bundleId: string, treezIds: string[], cartTreezIds: string[]) => {
      if (loadingBundleProducts.has(bundleId) || bundleProductsMap.has(bundleId)) {
        return;
      }

      setLoadingBundleProducts((prev) => new Set(prev).add(bundleId));

      try {
        const storeKey = localStorage.getItem('selectedStore') || selectedStore;
        const otherTreezIds = treezIds.filter((id) => !cartTreezIds.includes(id));

        if (otherTreezIds.length === 0) {
          setBundleProductsMap((prev) => new Map(prev).set(bundleId, []));
          return;
        }

        const productPromises = otherTreezIds.map(async (treezId) => {
          try {
            const response = await fetch(
              `/api/product_by_treez_id?treez_id=${treezId}&store=${storeKey}`,
            );
            if (!response.ok) return null;
            const data = await response.json();

            if (!data.found) return null;

            return {
              id: data.product.id,
              name: data.product.name,
              image: data.product.primary_image || data.product.image,
              price: data.product.price,
              treez_id: treezId,
              inStock: data.inStock,
            };
          } catch (error) {
            console.error(`Error fetching bundle product ${treezId}:`, error);
            return null;
          }
        });

        const results = await Promise.all(productPromises);
        const validProducts = results.filter(
          (p): p is BundleProductItem => p !== null && p.inStock,
        );

        setBundleProductsMap((prev) => new Map(prev).set(bundleId, validProducts));
      } catch (error) {
        console.error('Error fetching bundle products:', error);
      } finally {
        setLoadingBundleProducts((prev) => {
          const next = new Set(prev);
          next.delete(bundleId);
          return next;
        });
      }
    },
    [loadingBundleProducts, bundleProductsMap, selectedStore],
  );

  // Add bundle product to cart
  const handleAddBundleProduct = async (product: BundleProductItem) => {
    setAddingToCart(product.id);

    try {
      const storeKey = localStorage.getItem('selectedStore');
      const order_id = storeKey
        ? localStorage.getItem(`token_${storeKey}`) || 'default_order_id'
        : 'default_order_id';

      const payload = {
        actionType: 'ADD' as const,
        order_id,
        product_id: product.id, // Use Typesense document ID for Skunkworks
        quantity: '1',
      };

      const { data, wasRetried } = await executeCartOperation(payload);

      if (wasRetried && storeKey) {
        const newToken = data?.addedCart?.cart?.id;
        if (newToken) {
          localStorage.setItem(`token_${storeKey}`, newToken);
        }
      }

      incrementCount(1);
      toast.success(`Added ${product.name.split(' - ')[0]} to cart`);
      setAddedProducts((prev) => new Set(prev).add(product.id));
      await fetchCartItems();
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  // Analyze bundles in cart
  const bundleAnalysis = useMemo((): BundleAnalysis[] => {
    if (!bundleDataLoaded) return [];

    const bundleGroups = new Map<string, CartItem[]>();

    cartItems.forEach((item) => {
      if (item.budflow_mup?.active && item.budflow_mup?.rule_id) {
        const ruleId = item.budflow_mup.rule_id;
        if (!bundleGroups.has(ruleId)) {
          bundleGroups.set(ruleId, []);
        }
        bundleGroups.get(ruleId)!.push(item);
      }
    });

    const analyses: BundleAnalysis[] = [];

    bundleGroups.forEach((items, ruleId) => {
      const firstItem = items[0];
      if (!firstItem.budflow_mup) return;

      const mup = firstItem.budflow_mup;
      const requiredQuantity = mup.rule_config.quantity || 0;
      const isPercentOff = mup.rule_type === 'buy_x_get_percent_off';
      const isFixedPrice = mup.rule_type === 'buy_x_get_for_y';
      const isAmountOff = mup.rule_type === 'buy_x_items_get_amount_off';
      const isSpendBased = mup.rule_type === 'spend_x_get_y_off';

      const currentQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      // Current discounted price (what customer is currently paying)
      const currentDiscountedPrice = items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
      );
      // Original price before any discounts (price_sell)
      const originalPrice = items.reduce(
        (sum, item) => sum + (item.price_sell || item.price) * item.quantity,
        0,
      );

      let completeBundles = 0;
      let isComplete = false;
      let savings = 0;
      let bundlePrice = currentDiscountedPrice;
      let missingCount = 0;
      let mupPriceIfApplied = originalPrice; // What the price would be with MUP applied to original price

      if (isSpendBased) {
        const minimumSpend = mup.rule_config.minimum_spend || 0;
        const discountAmount = mup.rule_config.discount_amount || 0;

        isComplete = originalPrice >= minimumSpend;
        if (isComplete) {
          mupPriceIfApplied = originalPrice - discountAmount;
          // Only apply savings if MUP price is better than current discounted price
          if (mupPriceIfApplied < currentDiscountedPrice) {
            savings = currentDiscountedPrice - mupPriceIfApplied;
            bundlePrice = mupPriceIfApplied;
          }
        }
        missingCount = isComplete ? 0 : 1;
      } else if (requiredQuantity > 0) {
        completeBundles = Math.floor(currentQuantity / requiredQuantity);
        isComplete = completeBundles > 0;

        if (isComplete) {
          // Calculate MUP price based on original price (price_sell)
          const originalPriceForCompleteBundles =
            (originalPrice / currentQuantity) * (completeBundles * requiredQuantity);
          const currentPriceForCompleteBundles =
            (currentDiscountedPrice / currentQuantity) * (completeBundles * requiredQuantity);

          if (isPercentOff && mup.rule_config.discount_percent) {
            mupPriceIfApplied =
              originalPriceForCompleteBundles * (1 - mup.rule_config.discount_percent / 100);
          } else if (isFixedPrice && mup.rule_config.target_price) {
            mupPriceIfApplied = mup.rule_config.target_price * completeBundles;
          } else if (isAmountOff && mup.rule_config.discount_amount) {
            mupPriceIfApplied =
              originalPriceForCompleteBundles - mup.rule_config.discount_amount * completeBundles;
          }

          // BEST DEAL WINS: Only apply bundle savings if MUP price is better than current discounted price
          if (mupPriceIfApplied < currentPriceForCompleteBundles) {
            savings = currentPriceForCompleteBundles - mupPriceIfApplied;
            bundlePrice = mupPriceIfApplied;
          } else {
            // Current discount is better, no additional savings from MUP
            savings = 0;
            bundlePrice = currentPriceForCompleteBundles;
          }
        }

        missingCount = requiredQuantity - (currentQuantity % requiredQuantity);
        if (currentQuantity >= requiredQuantity) {
          missingCount = 0;
        }
      }

      // For incomplete bundles, check if MUP would even be worth it
      // Calculate what the MUP price would be if bundle was completed
      let mupWouldBeBetterDeal = true;
      if (!isComplete && requiredQuantity > 0 && !isSpendBased) {
        // Estimate: assume adding more items at average original price
        const avgOriginalPrice = originalPrice / currentQuantity;
        const avgCurrentPrice = currentDiscountedPrice / currentQuantity;
        const projectedOriginalTotal = avgOriginalPrice * requiredQuantity;
        const projectedCurrentTotal = avgCurrentPrice * requiredQuantity;

        let projectedMupPrice = projectedOriginalTotal;
        if (isPercentOff && mup.rule_config.discount_percent) {
          projectedMupPrice = projectedOriginalTotal * (1 - mup.rule_config.discount_percent / 100);
        } else if (isFixedPrice && mup.rule_config.target_price) {
          projectedMupPrice = mup.rule_config.target_price;
        } else if (isAmountOff && mup.rule_config.discount_amount) {
          projectedMupPrice = projectedOriginalTotal - mup.rule_config.discount_amount;
        }

        // If MUP price wouldn't be better than current discounted price, don't show the upsell
        mupWouldBeBetterDeal = projectedMupPrice < projectedCurrentTotal;
      }

      const savingsPercent =
        currentDiscountedPrice > 0 ? (savings / currentDiscountedPrice) * 100 : 0;
      const bundleProductTreezIds = mup.products || [];
      const cartTreezIds = items.filter((item) => item.treez_id).map((item) => item.treez_id!);

      // Calculate savings from original price (for display)
      const savingsFromOriginal = isComplete && savings > 0 ? originalPrice - bundlePrice : 0;

      // Only add to analyses if MUP would be a better deal (or for complete bundles with actual savings)
      if (mupWouldBeBetterDeal || (isComplete && savings > 0)) {
        analyses.push({
          bundleId: ruleId,
          bundleName: mup.rule_name,
          requiredQuantity,
          currentQuantity,
          bundlePrice,
          regularPrice: currentDiscountedPrice,
          originalPrice,
          savings: Math.max(0, savings),
          savingsFromOriginal: Math.max(0, savingsFromOriginal),
          savingsPercent,
          isComplete,
          itemsInCart: items,
          missingCount,
          ruleType: mup.rule_type,
          discountPercent: mup.rule_config.discount_percent,
          discountAmount: mup.rule_config.discount_amount,
          minimumSpend: mup.rule_config.minimum_spend,
          currentSpend: currentDiscountedPrice,
          targetPrice: mup.rule_config.target_price,
          bundleProductTreezIds,
          cartTreezIds,
        });
      }
    });

    return analyses;
  }, [cartItems, bundleDataLoaded]);

  // Calculate total bundle savings (incremental - for price calculation)
  const totalBundleSavings = useMemo(() => {
    return bundleAnalysis.reduce((sum, bundle) => sum + bundle.savings, 0);
  }, [bundleAnalysis]);

  // Calculate total savings from original price (for display)
  const totalSavingsFromOriginal = useMemo(() => {
    return bundleAnalysis.reduce((sum, bundle) => sum + bundle.savingsFromOriginal, 0);
  }, [bundleAnalysis]);

  // Get incomplete bundles for upsell
  const incompleteBundles = useMemo(() => {
    return bundleAnalysis.filter((b) => {
      if (b.ruleType === 'spend_x_get_y_off') {
        return b.minimumSpend && b.currentSpend && b.currentSpend < b.minimumSpend;
      }
      return !b.isComplete && b.missingCount > 0;
    });
  }, [bundleAnalysis]);


  useEffect(() => {
    fetchCartItems();
  }, []);

  // Optimistic add: show a just-added item INSTANTLY from the product card's own
  // data while the real cart GET syncs in the background. The budflow cart gql is
  // ~0.8s, so this bridges the gap for a snappy add-to-cart (the GET replaces the
  // optimistic line with the authoritative one, matched away by the full refetch).
  useEffect(() => {
    const onOptimisticAdd = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      // Title-case the (often ALL-CAPS) card name so the optimistic line reads
      // close to the server's `name_sanitized` — minimises the visible swap when
      // the background GET replaces it.
      const titleCase = (s: string) =>
        s
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase())
          .replace(/\b(\d+(?:\.\d+)?)([a-z])\b/gi, (_m, n, u) => n + u.toUpperCase());
      const rawName = (d.name_sanitized || d.name || '').toString();
      const label = d.name_sanitized ? rawName : titleCase(rawName);
      if (!label) return;
      setCartItems((prev) => {
        const key = label.toLowerCase();
        const idx = prev.findIndex(
          (i) => (i.name_sanitized || i.name || '').toLowerCase() === key,
        );
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], quantity: next[idx].quantity + (d.quantity || 1) };
          return next;
        }
        // Resolve the image the SAME way real cart lines do (line 943 renders
        // `budflow_image_url || primary_image`, with the getFillerImage brand-logo
        // fallback) so there's no image pop-in when the GET swaps the line in.
        const resolvedImage =
          d.primary_image ||
          getFillerImage(d.brand || 'Unknown Brand', d.category, getBrandLogo(d.brand || 'Unknown Brand'));
        // Append (bottom) — the server returns lines oldest→newest (UUIDv7 id
        // order), so a newly-added item belongs at the end. Prepending would make
        // it jump when the GET syncs.
        return [
          ...prev,
          {
            id: `optimistic-${Date.now()}`,
            order_id: '',
            product_id: '',
            onsale: d.onsale,
            primary_image: resolvedImage,
            budflow_image_url: resolvedImage,
            name: d.name || label,
            name_sanitized: label, // the rendered field — title-cased to match the server
            brand: d.brand || '',
            price: typeof d.price === 'number' ? d.price : 0,
            price_sell: typeof d.price_sell === 'number' ? d.price_sell : d.price || 0,
            quantity: d.quantity || 1,
            classification: d.classification || '',
            category: d.category || '',
          },
        ];
      });
    };
    window.addEventListener('cart:optimistic-add', onOptimisticAdd);
    return () => window.removeEventListener('cart:optimistic-add', onOptimisticAdd);
  }, []);

  // Update quantity mutation
  const updateQuantity = async (id: string, delta: number) => {
    const item = cartItems.find((x) => x.id === id);
    if (!item) return;

    if (item.quantity + delta <= 0) {
      await removeProduct(id);
      return;
    }

    // Optimistic: move the quantity INSTANTLY. The old code awaited the UPDATE
    // POST *and* a full fetchCartItems() GET before the number changed (~1.5s of
    // two sequential round-trips). We update state now and reconcile on the next
    // cart open; on failure we revert. UPDATE is keyed on the stable product_id,
    // so it survives the backend's orderline-id regeneration. No success re-fetch
    // (matches removeProduct) — avoids a slow round-trip and rapid-click races.
    // Apply the delta functionally so rapid successive clicks accumulate correctly
    // (rather than both reading the same stale closure quantity).
    setCartItems((prev) =>
      prev.map((x) => (x.id === id ? { ...x, quantity: Math.max(1, x.quantity + delta) } : x)),
    );
    if (delta > 0) {
      incrementCount(delta);
    } else {
      decrementCount(Math.abs(delta));
    }
    setLiveMessage(`Quantity: ${item.quantity + delta}`);

    const revert = () => {
      setCartItems((prev) =>
        prev.map((x) => (x.id === id ? { ...x, quantity: Math.max(1, x.quantity - delta) } : x)),
      );
      if (delta > 0) {
        decrementCount(delta);
      } else {
        incrementCount(Math.abs(delta));
      }
    };

    try {
      const body = {
        actionType: 'UPDATE',
        order_id: item.order_id,
        product_id: item.product_id,
        quantity: String(delta),
      };

      const response = await cartFetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const responseData = await response.clone().json();

      // Check for errors: API returns both non-OK statuses AND 200 with error in body
      if (!response.ok || responseData?.error) {
        revert();
        const parsedError = parseCartError(responseData, response);
        if (parsedError.shouldShowToast) {
          toast.error(parsedError.message);
        }
        return;
      }
    } catch (error: any) {
      console.error('Error updating cart quantity:', error);
      revert();
    }
  };

  const removeProduct = async (id: string) => {
    const item = cartItems.find((x) => x.id === id);
    if (!item) return;

    // Optimistic: drop the line from the UI instantly so remove feels snappy
    // (the budflow delete + refetch take ~0.9s). We reconcile / restore below.
    setCartItems((prev) => prev.filter((x) => x.id !== id));
    decrementCount(item.quantity);
    setLiveMessage(`Removed ${item.name} from cart.`);

    // An optimistic-add line (no real orderline id yet) has nothing to delete.
    if (!item.id || item.id.startsWith('optimistic-')) return;

    try {
      const response = await cartFetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Remove by the STABLE (order_id, product_id), not the orderline id: budflow
        // regenerates every orderline id on each write, so a held line id goes stale
        // after any add/+/- and would delete nothing (item resurrects). line_id is
        // sent too as a fallback.
        body: JSON.stringify({
          actionType: 'REMOVE',
          order_id: item.order_id,
          product_id: item.product_id,
          line_id: item.id,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to remove product: ${response.statusText}`);
      }

      // 🔥 Enhanced Analytics - Both GA4 + Klaviyo tracking
      analytics.trackRemoveFromCartComplete({
        id: item.product_id,
        name: item.name_sanitized || item.name,
        category: item.category,
        brand: item.brand,
        price: item.price || item.price_sell,
        quantity: item.quantity,
      });

      // NB: intentionally NO re-fetch here. The optimistic filter already removed
      // the line and decremented the badge, and the other lines are untouched. A
      // background GET would RACE rapid removes — a GET fired after one delete but
      // before the next commits returns the not-yet-deleted line and it flashes
      // back. We only re-sync on the failure path below.
    } catch (error) {
      console.error('Error removing product:', error);
      // Failed — restore the real cart so the UI reflects reality.
      incrementCount(item.quantity);
      fetchCartItems();
    }
  };

  // Calculate MUP price per item for items in complete bundles
  const itemMupPrices = useMemo(() => {
    const priceMap = new Map<string, number>();

    bundleAnalysis.forEach((bundle) => {
      if (bundle.isComplete && bundle.savingsFromOriginal > 0) {
        // Calculate per-item MUP price based on original price
        const perItemOriginalPrice = bundle.originalPrice / bundle.currentQuantity;
        let perItemMupPrice = perItemOriginalPrice;

        if (bundle.ruleType === 'buy_x_get_percent_off' && bundle.discountPercent) {
          perItemMupPrice = perItemOriginalPrice * (1 - bundle.discountPercent / 100);
        } else if (bundle.ruleType === 'buy_x_get_for_y' && bundle.targetPrice) {
          perItemMupPrice = bundle.targetPrice / bundle.requiredQuantity;
        } else if (bundle.ruleType === 'buy_x_items_get_amount_off' && bundle.discountAmount) {
          perItemMupPrice = perItemOriginalPrice - bundle.discountAmount / bundle.requiredQuantity;
        }

        bundle.itemsInCart.forEach((item) => {
          priceMap.set(item.id, perItemMupPrice);
        });
      }
    });

    return priceMap;
  }, [bundleAnalysis]);

  // Check if any bundle is active (for display purposes)
  const hasActiveMupBundle = bundleAnalysis.some((b) => b.isComplete && b.savingsFromOriginal > 0);

  // Pricing calculations
  // When MUP is active, show original price as subtotal, then subtract bundle savings
  const originalSubtotal = cartItems.reduce(
    (sum, item) => sum + (item.price_sell || item.price) * item.quantity,
    0,
  );
  const currentSubtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Use original subtotal when MUP bundles are active, otherwise use current discounted subtotal
  const displaySubtotal = hasActiveMupBundle ? originalSubtotal : currentSubtotal;
  const subtotalAfterBundles = hasActiveMupBundle
    ? originalSubtotal - totalSavingsFromOriginal
    : currentSubtotal - totalBundleSavings;
  const tax = subtotalAfterBundles * TAX_RATE;
  const total = subtotalAfterBundles + tax;

  return (
    <>
      {/* Overlay */}
      {isCartOpen && (
        <div
          style={{ zIndex: 4000 }}
          className="fixed inset-0 z-104 bg-gray-700/60"
          onClick={closeCart}
        />
      )}

      {/* Side Cart */}
      <div
        ref={sideCartRef}
        style={{ zIndex: 4000 }}
        onClick={(e) => e.stopPropagation()}
        className={`text-catalystblue fixed top-0 right-0 bottom-0 z-105 flex h-full w-full transform flex-col bg-white px-4 shadow-lg transition-transform duration-300 ease-in-out sm:w-[400px] sm:px-6 md:w-[450px] md:px-8 ${
          isCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        // When closed the drawer is only slid off-screen, so mark it inert to pull its
        // controls out of the tab order and accessibility tree (otherwise Tab reaches the
        // hidden cart "behind" the page). React 18 needs the attribute spread form.
        {...(!isCartOpen ? ({ inert: '' } as Record<string, string>) : {})}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        aria-describedby="cart-hint"
      >
        {/* Short, STATIC description read on open — keeps announcement 1 brief and (being
            static) can't report a stale "empty" during the async load. It also gives the
            screen reader something to read instead of the close button. The detailed contents
            (item count + total) are announced separately via the live region once loaded. */}
        <div id="cart-hint" className="sr-only">
          Press Escape to close.
        </div>
        {/* Live region for action + contents announcements. Inside the dialog so it announces
            reliably while the modal is open; the message is cleared shortly after it's set (see
            effect below) so stale text is never read when focus lands on the drawer. */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {liveMessage}
        </div>
        {/* Header */}
        <div className="text-catalystblue font-futura flex justify-between pt-6 md:pt-8">
          {/* aria-hidden: the dialog's aria-label ("Shopping cart") already names it for the
              screen reader, so the visible title would otherwise be read a second time. */}
          <div id="cart-title" className="text-lg" aria-hidden="true">
            Shopping Cart
          </div>
          <button
            id="cart-close-button"
            aria-label="Close cart menu"
            className="relative before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
            onClick={closeCart}
          >
            <img src="/images/X-Icon.svg" alt="" className="h-7 w-7" />
          </button>
        </div>

        <div className="menu-line mt-2 mb-4 h-0.5 w-full transform rounded-full bg-[#CCFF00] transition-transform duration-700 ease-in-out"></div>

        {/* Cart Items */}
        <div className="-mx-4 min-h-0 flex-1 overflow-y-auto px-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8">
          {isCartOpen && <CartRewardsCallout onLeaveCart={closeCart} />}
          {cartItems.length === 0 ? (
            <p className="mt-10 text-center text-gray-700">Your cart is empty.</p>
          ) : (
            <div className="space-y-2 pb-3">
              {cartItems.map((item) => (
                <CartItemRow
                  key={item.id}
                  name={item.name_sanitized || item.name}
                  brand={item.brand}
                  category={item.category}
                  image={item.budflow_image_url || item.primary_image}
                  lightLogo={needsLightLogoTile(
                    item.budflow_image_url || item.primary_image,
                    item.brand,
                    item.category,
                    getBrandLogo(item.brand),
                  )}
                  price={itemMupPrices.get(item.id) ?? item.price}
                  originalPrice={item.price_sell}
                  discounted={itemMupPrices.has(item.id) || !!item.onsale}
                  quantity={item.quantity}
                  onIncrease={() => updateQuantity(item.id, 1)}
                  onDecrease={() => updateQuantity(item.id, -1)}
                  onRemove={() => removeProduct(item.id)}
                />
              ))}
            </div>
          )}

          {/* Bundle Upsell Section */}
          {incompleteBundles.length > 0 && (
            <div className="border-t border-gray-200 bg-gray-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <div className="bg-catalystblue flex h-6 w-6 items-center justify-center rounded-full text-white">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <span className="font-poppins text-catalystblue text-sm font-bold">
                  Complete Your Bundle & Save!
                </span>
              </div>
              {incompleteBundles.map((bundle) => {
                const isPercentOff = bundle.ruleType === 'buy_x_get_percent_off';
                const isFixedPrice = bundle.ruleType === 'buy_x_get_for_y';
                const isAmountOff = bundle.ruleType === 'buy_x_items_get_amount_off';
                const isSpendBased = bundle.ruleType === 'spend_x_get_y_off';

                // Calculate estimated savings based on rule type - use targetPrice for fixed price bundles
                let estimatedSavings = 0;
                if (bundle.currentQuantity > 0 && bundle.requiredQuantity > 0) {
                  const estimatedRegularPrice =
                    (bundle.regularPrice / bundle.currentQuantity) * bundle.requiredQuantity;
                  if (isPercentOff && bundle.discountPercent) {
                    estimatedSavings = (estimatedRegularPrice * bundle.discountPercent) / 100;
                  } else if (isFixedPrice && bundle.targetPrice) {
                    // Use the actual target price from the rule, not bundlePrice (which is regularPrice for incomplete bundles)
                    estimatedSavings = estimatedRegularPrice - bundle.targetPrice;
                  } else if (isAmountOff && bundle.discountAmount) {
                    estimatedSavings = bundle.discountAmount;
                  }
                }
                if (isSpendBased && bundle.discountAmount) {
                  estimatedSavings = bundle.discountAmount;
                }

                // Calculate progress for display
                let progressPercent = 0;
                let progressText = '';
                let actionText = '';
                let badgeText = '';

                if (isSpendBased && bundle.minimumSpend) {
                  const remaining = bundle.minimumSpend - (bundle.currentSpend || 0);
                  progressPercent = Math.min(
                    100,
                    ((bundle.currentSpend || 0) / bundle.minimumSpend) * 100,
                  );
                  progressText = `$${(bundle.currentSpend || 0).toFixed(2)} of $${bundle.minimumSpend.toFixed(2)} spent`;
                  actionText = `Spend $${remaining.toFixed(2)} more to get $${bundle.discountAmount} off`;
                  badgeText = `$${bundle.discountAmount} OFF`;
                } else if (isAmountOff && bundle.discountAmount) {
                  progressPercent = Math.min(
                    100,
                    (bundle.currentQuantity / bundle.requiredQuantity) * 100,
                  );
                  progressText = `${bundle.currentQuantity} of ${bundle.requiredQuantity} items`;
                  actionText = `Add ${bundle.missingCount} more to get $${bundle.discountAmount} off`;
                  badgeText = `$${bundle.discountAmount} OFF`;
                } else if (isPercentOff && bundle.discountPercent) {
                  progressPercent = Math.min(
                    100,
                    (bundle.currentQuantity / bundle.requiredQuantity) * 100,
                  );
                  progressText = `${bundle.currentQuantity} of ${bundle.requiredQuantity} items`;
                  actionText = `Add ${bundle.missingCount} more to get ${bundle.discountPercent}% off`;
                  badgeText = `${bundle.discountPercent}% OFF`;
                } else {
                  // isFixedPrice - use targetPrice for the action text
                  const targetPriceDisplay =
                    bundle.targetPrice?.toFixed(2) || bundle.bundlePrice.toFixed(2);
                  progressPercent = Math.min(
                    100,
                    (bundle.currentQuantity / bundle.requiredQuantity) * 100,
                  );
                  progressText = `${bundle.currentQuantity} of ${bundle.requiredQuantity} items`;
                  actionText = `Add ${bundle.missingCount} more to get ${bundle.requiredQuantity} for $${targetPriceDisplay}`;
                  badgeText = `SAVE $${estimatedSavings.toFixed(2)}`;
                }

                // Check if this bundle is expanded
                const isExpanded = expandedBundles.has(bundle.bundleId);
                const bundleProducts = bundleProductsMap.get(bundle.bundleId) || [];
                const isLoadingProducts = loadingBundleProducts.has(bundle.bundleId);
                const hasLoadedProducts = bundleProductsMap.has(bundle.bundleId);

                // Handle expand click - fetch products if needed
                const handleExpandClick = () => {
                  toggleBundleExpanded(bundle.bundleId);
                  // Fetch products when expanding if not already loaded
                  if (
                    !isExpanded &&
                    !hasLoadedProducts &&
                    !isLoadingProducts &&
                    bundle.bundleProductTreezIds.length > 0
                  ) {
                    fetchBundleProducts(
                      bundle.bundleId,
                      bundle.bundleProductTreezIds,
                      bundle.cartTreezIds,
                    );
                  }
                };

                return (
                  <div
                    key={bundle.bundleId}
                    className="border-catalystblue mb-3 rounded-lg border-2 bg-white p-4 last:mb-0"
                  >
                    <button
                      onClick={handleExpandClick}
                      className="w-full text-left focus:outline-none"
                      aria-expanded={isExpanded}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-poppins text-catalystblue text-sm font-bold">
                            {bundle.bundleName}
                          </p>
                          <p className="mt-1 text-sm text-gray-600">{actionText}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="bg-catalystblue rounded px-2.5 py-1.5 text-sm font-bold text-white">
                            {badgeText}
                          </span>
                          <svg
                            className={`text-catalystblue h-5 w-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-3 h-2.5 w-full rounded-full bg-gray-200">
                        <div
                          className="bg-catalystblue h-2.5 rounded-full transition-all duration-300"
                          style={{
                            width: `${progressPercent}%`,
                          }}
                        />
                      </div>
                      <p className="mt-1.5 text-sm text-gray-500">{progressText}</p>
                    </button>

                    {/* Scrollable Bundle Products Container - Only show when expanded */}
                    {isExpanded && (
                      <>
                        {isLoadingProducts ? (
                          <div className="mt-4 flex items-center justify-center py-4">
                            <div className="border-t-catalystblue h-6 w-6 animate-spin rounded-full border-2 border-gray-200" />
                            <span className="ml-2 text-sm text-gray-600">Loading products...</span>
                          </div>
                        ) : bundleProducts.length > 0 ? (
                          <div className="mt-4">
                            <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent flex gap-3 overflow-x-auto pb-2">
                              {bundleProducts.map((product) => {
                                const isInCart = cartItems.some(
                                  (item) => item.product_id === product.id,
                                );
                                const wasJustAdded = addedProducts.has(product.id);
                                return (
                                  <div
                                    key={product.id}
                                    className={`w-[140px] flex-shrink-0 rounded-lg border bg-white p-3 transition-colors ${
                                      isInCart
                                        ? 'border-2 border-green-500'
                                        : 'hover:border-catalystblue border-gray-200'
                                    }`}
                                  >
                                    {/* In Cart Badge */}
                                    {isInCart && (
                                      <div className="mb-2 flex items-center justify-center gap-1 rounded bg-green-100 py-1 text-[10px] font-bold text-green-700">
                                        <svg
                                          className="h-3 w-3"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                        In Cart
                                      </div>
                                    )}
                                    {/* Clickable Product Area */}
                                    <button
                                      onClick={() => {
                                        openProductModal(product.id);
                                        closeCart();
                                      }}
                                      className="focus-visible:ring-catalystblue w-full rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                                      aria-label={`View ${product.name} details`}
                                    >
                                      {/* Product Image */}
                                      <div className="mb-2 h-20 w-full overflow-hidden rounded-md bg-gray-100">
                                        {product.image ? (
                                          <img
                                            src={product.image}
                                            alt={product.name}
                                            className="h-full w-full object-cover transition-transform hover:scale-105"
                                            loading="lazy"
                                          />
                                        ) : (
                                          <div className="flex h-full w-full items-center justify-center bg-gray-200">
                                            <span className="text-xl text-gray-400">🌿</span>
                                          </div>
                                        )}
                                      </div>
                                      {/* Product Name */}
                                      <p className="font-poppins text-catalystblue mb-1.5 line-clamp-2 text-xs leading-tight font-bold hover:underline">
                                        {product.name.split(' - ')[0]}
                                      </p>
                                    </button>
                                    {/* Price */}
                                    <p className="mb-2 text-sm font-bold text-black">
                                      ${product.price.toFixed(2)}
                                    </p>
                                    {/* Add Button */}
                                    <button
                                      onClick={() => handleAddBundleProduct(product)}
                                      disabled={addingToCart === product.id}
                                      className="border-catalystblue bg-catalystblue hover:text-catalystblue w-full rounded-md border py-2 text-xs font-bold text-white transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                                      aria-label={`Add ${product.name} to cart`}
                                    >
                                      {addingToCart === product.id ? (
                                        <div className="flex items-center justify-center gap-1">
                                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                        </div>
                                      ) : (
                                        <span className="flex items-center justify-center gap-1">
                                          <svg
                                            className="h-4 w-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                            />
                                          </svg>
                                          ADD
                                        </span>
                                      )}
                                    </button>
                                    {/* Just Added indicator */}
                                    {wasJustAdded && (
                                      <div className="mt-1 flex items-center justify-center gap-1 text-[10px] font-bold text-green-600">
                                        <svg
                                          className="h-3 w-3"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                        Added
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <p className="mt-4 text-center text-sm text-gray-500">
                            No other bundle products available
                          </p>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Pricing Summary & Checkout */}
        <div className="font-futura flex-shrink-0 border-t border-gray-200 bg-white pt-4 pb-6">
          <div>
            {/* Promotion Code Message */}
            {promotionCode && cartItems.length > 0 && (
              <div className="mx-4 mb-3 rounded-md border border-green-200 bg-green-50 p-3">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-5 w-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800">
                      Coupon code <span className="font-bold">{promotionCode}</span> will be applied
                      at checkout
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Bundle Pricing Breakdown */}
            {totalSavingsFromOriginal > 0 && (
              <div className="mx-4 mb-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="text-gray-600">${displaySubtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">Bundle Savings:</span>
                  <span className="text-green-600">-${totalSavingsFromOriginal.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="flex justify-between px-4 text-xl font-bold">
              <span>Total (Includes taxes):</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <div className="mt-4 px-4">
              <button
                className="bg-catalystblue hover:text-catalystdarkblue relative w-full rounded-xs py-2 text-lg text-white transition-all duration-500 ease-in-out hover:bg-[#CCFF00]"
                onClick={() => {
                  setIsCheckoutLoading(true);

                  // 🔥 GA4 Enhanced Ecommerce - begin_checkout event
                  trackBeginCheckout(cartItems, total);

                  // Small delay to ensure tracking fires before navigation
                  setTimeout(() => {
                    // Close product modal but keep cart open
                    closeProductModal();
                    // Navigate to checkout
                    window.location.href = '/checkout';
                  }, 100);
                }}
                disabled={isCheckoutLoading}
              >
                {isCheckoutLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    <span className="ml-2">Loading...</span>
                  </div>
                ) : (
                  'Go to Checkout'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
